
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import {
  getAccountManagerByLocale,
  getProduct,
  getProducts,
  getRelatedProducts,
} from '@/lib/utilities/repository';
import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps } from 'next/types';
import { AccountManager, IProdView, Product } from '@/lib/types/bevmaq.types';
import DOMPurify from 'isomorphic-dompurify';
import styles from '../../../styles/Wysiwyg.module.sass';
import { BuyFormModal } from '@/components/Products/BuyModal/BuyFormModal';
import { Loading } from '@/components/Loading';
import { BreadCrumbs } from '@/components/Products/BreadCrumbs';
import { NextSeo } from 'next-seo';
import PriceFormat from '@/components/Products/Price';
import { isArray, last } from 'lodash-es';
import useTranslation from 'next-translate/useTranslation';
import { ProductImages } from '@/components/Products/Images/ProductImages';
import { ProductListItem } from '@/components/Products/ProductList';
import { Documents } from '@/components/Products/Documents';
import { ProductContact } from '@/components/Products/ProductContact';
import { useEffect } from 'react';
import { getGTMProductData, pushDataLayer } from '@/lib/utilities/gtm';
interface ProductViewProps {
  productData: IProdView;
  relatedProducts: Product[];
  accountManager: AccountManager;
}

function ProductView({
  productData,
  relatedProducts,
  accountManager,
}: ProductViewProps) {
  const router = useRouter();
  const { t } = useTranslation();
  useEffect(() => {
    if (productData?.id) {
      pushDataLayer('view_item', {
        ecommerce: {
          value: productData.price?.amount,
          currency: productData.price?.currency,
          items: [getGTMProductData(productData)],
        },
      });
    }
  }, [productData]);
  if (router.isFallback) {
    return <Loading />;
  }
  const table = [
    {
      title: t('Country'),
      value: productData.country.name,
    },
    {
      title: t('Year'),
      value: productData.year,
    },
    {
      title: t('Category'),
      value: productData.category.name,
    },
    {
      title: t('Manufacturer'),
      value: productData.model.manufacturer.name,
    },
    {
      title: t('Model'),
      value: productData.model.name,
    },
    {
      title: t('Availability'),
      value: productData.available
        ? productData.available_from ?? t('Immediately')
        : t('common:Sold'),
    },
    {
      title: 'SKU',
      value: productData.sku,
    },
  ];
  return (
    <div className='bg-white p-4 md:mx-4 md:p-8'>
      <NextSeo
        title={productData.seo_title}
        description={productData.seo_description}
        canonical={router.asPath}
      />
      <BreadCrumbs product={productData} />
      <div className='grid md:grid-cols-3 md:gap-4 lg:gap-8'>
        <ProductImages product={productData} />
        <div className='space-y-4'>
          <div className=''>
            <h3 className='max-w-[85vw] break-words pt-4 text-2xl font-semibold text-gray-700'>
              {productData.title}
            </h3>
            {productData.price?.amount
              ? productData.available && (
                  <p className='mt-1 text-2xl text-gray-900'>
                    <PriceFormat price={productData.price} /> (ExWorks)
                  </p>
                )
              : productData.available_from != null &&
                t('common:Price Upon Request')}
          </div>
          {productData.available && <BuyFormModal product={productData} />}
          <div className='my-4 w-full overflow-hidden rounded-xl border-2'>
            {table.map((row) => (
              <div
                className='flex border-b-2 border-slate-200 last:border-0'
                key={row.title}
              >
                <h3 className='min-w-[50%] bg-slate-100 p-2'>{row.title}:</h3>
                <h3 className='break-words p-2'>{row.value}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className='mt-4' />
      <div className='grid gap-8 lg:grid-cols-3'>
        <div className='flex flex-col md:mx-16 lg:col-span-2'>
          <div
            className={styles.wysiwyg}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(productData.description, {
                FORBID_ATTR: ['style'],
              }),
            }}
          />
          <Documents documents={productData.documents} />
          <h4 className='mt-4 pt-4 text-sm font-light italic'>
            *{t('common:There may be differences')}
          </h4>
        </div>
        <div className=''>
          <div className='sticky top-[5em] md:h-[calc(100vh_-_6rem)] '>
            <ProductContact
              product={productData}
              accountManager={accountManager}
            />
          </div>
        </div>
      </div>
      <RelatedProducts relatedProducts={relatedProducts} />
    </div>
  );
}

function RelatedProducts({ relatedProducts }: { relatedProducts: Product[] }) {
  const { t } = useTranslation();
  return (
    <>
      {isArray(relatedProducts) && relatedProducts.length !== 0 && (
        <h3 className='title pt-4'>{t('Related Products')}</h3>
      )}
      <div
        className='grid grid-cols-1 gap-x-6 gap-y-10 pt-4 sm:grid-cols-2 lg:grid-cols-3 xl:gap-x-8'
        data-testid='related-product-list'
      >
        {relatedProducts.map((p) => (
          <ProductListItem key={p.id} p={p} />
        ))}
      </div>
    </>
  );
}

 const _getStaticProps: GetStaticProps = async ({ params, locale }) => {
  if (!params?.slug) {
    throw new Error('no slug');
  }
  const slug = params.slug.toString();
  // const sku = last(decodeURI(slug.split('_')) ?? 'no-slug';
  const sku = last(slug.split('_')) ?? 'no-slug';
  const product = await getProduct(sku, locale);
  const relatedProducts = await getRelatedProducts(sku, locale);
  const accountManager = await getAccountManagerByLocale(locale);

  if (!product) {
    return {
      props: {},
      notFound: true,
    };
  }
  // If the request was successful, return the posts
  // and revalidate at max every 120 seconds.
  return {
    props: {
      productData: product,
      relatedProducts: relatedProducts.results,
      accountManager: accountManager,
    },
    revalidate: 120,
    notFound: !product?.model?.name,
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  // Call an external API endpoint to get all products
  // so we have all slugs and can pre generate the pages
  const products = await getProducts({
    pageSize: process.env.CI ? 200 : 5,
    locale: 'en-US',
  });
  const paths: any = [];
  if (locales !== undefined) {
    products.results.forEach((product) => {
      locales.forEach((locale) => {
        paths.push({
          params: {
            slug: product.slug,
          },
          locale,
        });
      });
    });
  }
  // We'll pre-render only these 'paths' at build time.
  return {
    paths,
    fallback: 'blocking', // if you request /buy/slug-non-existent I will try to server side generate this page
  };
};

export default ProductView;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/buy/[slug]/index',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  